import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "./AuthContext";
import { supabase } from "./supabaseClient";
import { fetchAllPages } from "./supabasePaging";
import { useTeam } from "./useTeam";
import { subscribeWithResync, uniqueChannelName } from "./realtimeResync";
import { isMissingRelationError } from "./supabaseErrors";
import {
  CASE_TASK_SELECT,
  CaseTaskError,
  applyOpenTaskChange,
  applyTaskChange,
  isNewTaskForMe,
  isTaskForUser,
  type CaseTask,
  type CaseTaskType,
  type TaskChange,
} from "./caseTasks";

export interface CreateCaseTaskInput {
  caseId: string;
  taskType: CaseTaskType;
  note?: string | null;
  /** true = tâche « Moi » (assignée au manager lui-même) ; sinon équipe du centre du dossier. */
  assignToSelf?: boolean;
}

interface CaseTasksContextType {
  /** Toutes les tâches ouvertes (tous dossiers, toutes équipes). */
  openTasks: CaseTask[];
  /** Tâches ouvertes du « À faire » de l'utilisateur connecté (règle isTaskForUser). */
  myOpenTasks: CaseTask[];
  /** Dossiers sur lesquels l'utilisateur a une tâche ouverte (flamme de la carte Kanban). */
  myOpenTaskCaseIds: Set<string>;
  /** Centres dont l'utilisateur est membre. */
  myCenters: string[];
  /**
   * Tâches reçues EN DIRECT (INSERT realtime) pour moi et dont je ne suis pas l'auteur,
   * encore ouvertes et non écartées. Jamais celles du chargement initial ni d'un rattrapage.
   */
  newTasksForMe: CaseTask[];
  dismissNewTask: (id: string) => void;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  /** Les actions lèvent une CaseTaskError dont `message` est en français. */
  createTask: (input: CreateCaseTaskInput) => Promise<CaseTask>;
  completeTask: (taskId: string) => Promise<CaseTask>;
  reopenTask: (taskId: string) => Promise<CaseTask>;
  cancelTask: (taskId: string) => Promise<CaseTask>;
  setCasePriority: (caseId: string, priority: boolean) => Promise<void>;
  /** Interne : relaie les lignes modifiées localement à useCaseTaskHistory. */
  subscribeLocalChanges: (listener: (task: CaseTask) => void) => () => void;
}

const CaseTasksContext = createContext<CaseTasksContextType | undefined>(undefined);

function toChange(payload: { eventType: string; new: unknown; old: unknown }): TaskChange | null {
  if (payload.eventType === "INSERT" || payload.eventType === "UPDATE") {
    return { eventType: payload.eventType, new: payload.new as CaseTask };
  }
  if (payload.eventType === "DELETE") {
    const old = payload.old as Partial<CaseTask> | null;
    return old?.id ? { eventType: "DELETE", old: { id: old.id } } : null;
  }
  return null;
}

async function callTaskRpc(fn: string, args: Record<string, unknown>): Promise<CaseTask> {
  const { data, error } = await supabase.rpc(fn, args);
  if (error) throw new CaseTaskError(error);
  const row = (Array.isArray(data) ? data[0] : data) as CaseTask | null;
  if (!row?.id) throw new CaseTaskError({ message: "task_not_found" });
  return row;
}

export function CaseTasksProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const userId = user?.id ?? null;
  const { centersOfUser, loading: teamLoading } = useTeam();
  const myCenters = useMemo(() => centersOfUser(userId), [centersOfUser, userId]);

  const [openTasks, setOpenTasks] = useState<CaseTask[]>([]);
  const [newTaskQueue, setNewTaskQueue] = useState<CaseTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** Table case_tasks absente (front déployé avant la migration) : fonction masquée, sans requêtes. */
  const [tasksUnavailable, setTasksUnavailable] = useState(false);

  const requestVersion = useRef(0);
  /** Événements reçus pendant un chargement : réappliqués sur le résultat pour ne rien perdre. */
  const changesDuringLoad = useRef<TaskChange[] | null>(null);
  const userIdRef = useRef(userId);
  const centersRef = useRef(myCenters);
  const openTasksRef = useRef(openTasks);
  const localListeners = useRef(new Set<(task: CaseTask) => void>());
  const teamLoadingRef = useRef(teamLoading);
  /** Tâches d'équipe reçues en direct avant le chargement des équipes : évaluées une fois les équipes connues. */
  const awaitingTeams = useRef<CaseTask[]>([]);
  userIdRef.current = userId;
  centersRef.current = myCenters;
  teamLoadingRef.current = teamLoading;
  openTasksRef.current = openTasks;

  const load = useCallback(async (): Promise<void> => {
    const version = ++requestVersion.current;
    changesDuringLoad.current = [];
    try {
      const rows = await fetchAllPages<CaseTask>((from, to) =>
        supabase
          .from("case_tasks")
          .select(CASE_TASK_SELECT)
          .is("completed_at", null)
          .is("cancelled_at", null)
          .order("created_at", { ascending: false })
          .order("id")
          .range(from, to),
      );
      if (version !== requestVersion.current) return;
      const pending = changesDuringLoad.current ?? [];
      setOpenTasks(pending.reduce(applyOpenTaskChange, rows));
      setError(null);
    } catch (err) {
      if (version !== requestVersion.current) return;
      if (isMissingRelationError(err)) {
        console.warn("[CaseTasks] Table case_tasks absente (migration en attente) : tâches désactivées.");
        setOpenTasks([]);
        setError(null);
        setTasksUnavailable(true);
        return;
      }
      console.error("[CaseTasks] Chargement des tâches impossible :", err);
      setError("Impossible de charger les tâches. Nouvelle tentative au retour sur l'onglet.");
    } finally {
      if (version === requestVersion.current) {
        changesDuringLoad.current = null;
        setLoading(false);
      }
    }
  }, []);

  /** Applique une ligne (realtime ou retour de RPC) à l'état local. */
  const applyChange = useCallback((change: TaskChange) => {
    changesDuringLoad.current?.push(change);
    setOpenTasks((prev) => applyOpenTaskChange(prev, change));
    if (change.eventType === "DELETE" || change.eventType === "UPDATE") {
      const id = change.eventType === "DELETE" ? change.old.id : change.new.id;
      const closed = change.eventType === "DELETE" || !!change.new.completed_at || !!change.new.cancelled_at;
      if (closed) setNewTaskQueue((prev) => (prev.some((task) => task.id === id) ? prev.filter((task) => task.id !== id) : prev));
    }
  }, []);

  const emitLocal = useCallback((task: CaseTask) => {
    localListeners.current.forEach((listener) => listener(task));
  }, []);

  useEffect(() => {
    if (authLoading) return;
    requestVersion.current += 1;
    setOpenTasks([]);
    setNewTaskQueue([]);
    awaitingTeams.current = [];
    if (!userId || tasksUnavailable) {
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    void load();

    const channel = supabase.channel(uniqueChannelName("case-tasks")).on(
      "postgres_changes",
      { event: "*", schema: "public", table: "case_tasks" },
      (payload) => {
        const change = toChange(payload);
        if (!change) return;
        applyChange(change);
        if (change.eventType !== "INSERT") return;
        const row = change.new;
        if (isNewTaskForMe(row, userIdRef.current, centersRef.current)) {
          setNewTaskQueue((prev) => (prev.some((task) => task.id === row.id) ? prev : [...prev, row]));
        } else if (teamLoadingRef.current && !row.assigned_to && row.assigned_center) {
          // Mes centres ne sont pas encore connus : on décidera du pop-up après le chargement des équipes.
          awaitingTeams.current.push(row);
        }
      },
    );
    // Rechargement aussi au premier SUBSCRIBED : une tâche créée entre l'instantané du chargement
    // initial et le début de l'écoute n'est pas perdue (pas de pop-up pour ces tâches rattrapées).
    return subscribeWithResync(channel, () => void load(), { resyncOnFirstSubscribe: true });
  }, [authLoading, userId, tasksUnavailable, load, applyChange]);

  // Équipes chargées : pop-up pour les tâches d'équipe reçues en direct entre-temps, si elles sont pour moi.
  useEffect(() => {
    if (teamLoading || awaitingTeams.current.length === 0) return;
    const candidates = awaitingTeams.current;
    awaitingTeams.current = [];
    const fresh = candidates.filter((task) => isNewTaskForMe(task, userId, myCenters));
    if (fresh.length === 0) return;
    setNewTaskQueue((prev) => [...prev, ...fresh.filter((task) => !prev.some((queued) => queued.id === task.id))]);
  }, [teamLoading, userId, myCenters]);

  const dismissNewTask = useCallback((id: string) => {
    setNewTaskQueue((prev) => prev.filter((task) => task.id !== id));
  }, []);

  const createTask = useCallback(
    async ({ caseId, taskType, note, assignToSelf }: CreateCaseTaskInput) => {
      const row = await callTaskRpc("create_case_task", {
        p_case_id: caseId,
        p_task_type: taskType,
        p_note: note?.trim() ? note.trim() : null,
        p_assign_to_self: !!assignToSelf,
      });
      applyChange({ eventType: "INSERT", new: row });
      emitLocal(row);
      return row;
    },
    [applyChange, emitLocal],
  );

  /** Coche / annulation : la tâche quitte la liste tout de suite, rétablie si le serveur refuse. */
  const closeTask = useCallback(
    async (taskId: string, rpc: "complete_case_task" | "cancel_case_task") => {
      const previous = openTasksRef.current.find((task) => task.id === taskId) ?? null;
      if (previous) {
        const now = new Date().toISOString();
        const optimistic: CaseTask =
          rpc === "complete_case_task"
            ? { ...previous, completed_at: now, completed_by: userIdRef.current }
            : { ...previous, cancelled_at: now, cancelled_by: userIdRef.current };
        applyChange({ eventType: "UPDATE", new: optimistic });
        emitLocal(optimistic);
      }
      try {
        const row = await callTaskRpc(rpc, { p_task_id: taskId });
        applyChange({ eventType: "UPDATE", new: row });
        emitLocal(row);
        return row;
      } catch (err) {
        if (previous) {
          applyChange({ eventType: "UPDATE", new: previous });
          emitLocal(previous);
        }
        void load(); // réconcilie (ex. tâche déjà cochée par un collègue)
        throw err;
      }
    },
    [applyChange, emitLocal, load],
  );

  const completeTask = useCallback((taskId: string) => closeTask(taskId, "complete_case_task"), [closeTask]);
  const cancelTask = useCallback((taskId: string) => closeTask(taskId, "cancel_case_task"), [closeTask]);

  const reopenTask = useCallback(
    async (taskId: string) => {
      const row = await callTaskRpc("reopen_case_task", { p_task_id: taskId });
      applyChange({ eventType: "UPDATE", new: row });
      emitLocal(row);
      return row;
    },
    [applyChange, emitLocal],
  );

  const setCasePriority = useCallback(async (caseId: string, priority: boolean) => {
    const { error: rpcError } = await supabase.rpc("set_case_priority", { p_case_id: caseId, p_priority: priority });
    if (rpcError) throw new CaseTaskError(rpcError);
  }, []);

  const subscribeLocalChanges = useCallback((listener: (task: CaseTask) => void) => {
    localListeners.current.add(listener);
    return () => {
      localListeners.current.delete(listener);
    };
  }, []);

  const value = useMemo<CaseTasksContextType>(() => {
    const myOpenTasks = openTasks.filter((task) => isTaskForUser(task, userId, myCenters));
    const openIds = new Set(openTasks.map((task) => task.id));
    return {
      openTasks,
      myOpenTasks,
      myOpenTaskCaseIds: new Set(myOpenTasks.map((task) => task.case_id)),
      myCenters,
      // Encore ouverte et toujours pour moi (les équipes ont pu changer entre-temps).
      newTasksForMe: newTaskQueue.filter((task) => openIds.has(task.id) && isTaskForUser(task, userId, myCenters)),
      dismissNewTask,
      loading: authLoading || loading,
      error,
      refresh: load,
      createTask,
      completeTask,
      reopenTask,
      cancelTask,
      setCasePriority,
      subscribeLocalChanges,
    };
  }, [
    openTasks,
    userId,
    myCenters,
    newTaskQueue,
    dismissNewTask,
    authLoading,
    loading,
    error,
    load,
    createTask,
    completeTask,
    reopenTask,
    cancelTask,
    setCasePriority,
    subscribeLocalChanges,
  ]);

  return <CaseTasksContext.Provider value={value}>{children}</CaseTasksContext.Provider>;
}

export function useCaseTasks(): CaseTasksContextType {
  const context = useContext(CaseTasksContext);
  if (!context) throw new Error("useCaseTasks doit être utilisé dans un CaseTasksProvider");
  return context;
}

/**
 * Toutes les tâches d'un dossier (ouvertes + cochées + annulées), plus récentes d'abord.
 * Temps réel + actions locales (coche optimiste…) répercutées immédiatement.
 * Fonctionne aussi hors CaseTasksProvider (sans les actions locales).
 */
export function useCaseTaskHistory(caseId: string | null | undefined) {
  const context = useContext(CaseTasksContext);
  const subscribeLocalChanges = context?.subscribeLocalChanges;
  const [tasks, setTasks] = useState<CaseTask[]>([]);
  const [loading, setLoading] = useState(!!caseId);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const requestVersion = useRef(0);
  const changesDuringLoad = useRef<TaskChange[] | null>(null);

  const load = useCallback(async (): Promise<void> => {
    if (!caseId) return;
    const version = ++requestVersion.current;
    changesDuringLoad.current = [];
    try {
      const rows = await fetchAllPages<CaseTask>((from, to) =>
        supabase
          .from("case_tasks")
          .select(CASE_TASK_SELECT)
          .eq("case_id", caseId)
          .order("created_at", { ascending: false })
          .order("id")
          .range(from, to),
      );
      if (version !== requestVersion.current) return;
      setTasks((changesDuringLoad.current ?? []).reduce(applyTaskChange, rows));
      setError(null);
    } catch (err) {
      if (version !== requestVersion.current) return;
      if (isMissingRelationError(err)) {
        // Table case_tasks absente (migration en attente) : historique vide, sans requêtes.
        setTasks([]);
        setError(null);
        setUnavailable(true);
        return;
      }
      console.error("[CaseTaskHistory] Chargement impossible :", err);
      setError("Impossible de charger les tâches du dossier.");
    } finally {
      if (version === requestVersion.current) {
        changesDuringLoad.current = null;
        setLoading(false);
      }
    }
  }, [caseId]);

  useEffect(() => {
    requestVersion.current += 1;
    setTasks([]);
    setError(null);
    if (!caseId || unavailable) {
      setLoading(false);
      return;
    }
    setLoading(true);
    void load();

    const apply = (change: TaskChange) => {
      changesDuringLoad.current?.push(change);
      setTasks((prev) => applyTaskChange(prev, change));
    };
    const unsubscribeLocal = subscribeLocalChanges?.((task) => {
      if (task.case_id === caseId) apply({ eventType: "UPDATE", new: task });
    });
    const channel = supabase.channel(uniqueChannelName(`case-tasks-history-${caseId}`)).on(
      "postgres_changes",
      { event: "*", schema: "public", table: "case_tasks", filter: `case_id=eq.${caseId}` },
      (payload) => {
        const change = toChange(payload);
        if (change) apply(change);
      },
    );
    const cleanupChannel = subscribeWithResync(channel, () => void load(), { resyncOnFirstSubscribe: true });
    return () => {
      unsubscribeLocal?.();
      cleanupChannel();
    };
  }, [caseId, unavailable, load, subscribeLocalChanges]);

  return { tasks, loading, error, refresh: load };
}
